/**
 * BeezFy Collection Grid JS
 * Minimal JavaScript for grid interactions
 * Integrates with BeezFy centralized systems
 */

(function() {
  'use strict';

  // Cache DOM elements
  let gridElement;
  let filterForm;
  let sortSelect;
  let loadMoreBtn;
  let infiniteTrigger;
  let isLoading = false;
  let isRestoring = false;  // ripristino snapshot al back
  let appendCount = 0;      // quante volte si e' accodato via AJAX
  let maxAppends = null;    // cap letto da data-max-appends (settings.beezfy_loadmore_max_pages)

  // Initialize on DOM ready
  function init() {
    gridElement = document.querySelector('[data-products-grid]');
    if (!gridElement) return;

    // Setup event listeners
    setupFilters();
    setupSorting();
    setupPagination();
    setupViewMode();
    setupVariantCards();
    setupRecentlyViewed();

    // Initialize ATC system integration
    initializeATCIntegration();
  }

  // Setup filters
  function setupFilters() {
    const filterToggle = document.querySelector('[data-filters-toggle]');
    const filterDrawer = document.querySelector('[data-filters-drawer]');
    const filterClose = document.querySelector('[data-filters-close]');
    const filterOverlay = document.querySelector('[data-filters-overlay]');
    const filterApply = document.querySelector('[data-filters-apply]');
    const filterClear = document.querySelector('[data-filters-clear]');

    if (!filterToggle || !filterDrawer) return;

    // Toggle drawer
    filterToggle.addEventListener('click', () => {
      const isOpen = filterDrawer.getAttribute('aria-hidden') === 'false';
      filterDrawer.setAttribute('aria-hidden', !isOpen);
      filterToggle.setAttribute('aria-expanded', !isOpen);
      if (filterOverlay) {
        filterOverlay.style.display = isOpen ? 'none' : 'block';
      }
    });

    // Close drawer
    [filterClose, filterOverlay].forEach(el => {
      if (el) {
        el.addEventListener('click', () => {
          filterDrawer.setAttribute('aria-hidden', 'true');
          filterToggle.setAttribute('aria-expanded', 'false');
          if (filterOverlay) {
            filterOverlay.style.display = 'none';
          }
        });
      }
    });

    // Apply filters
    if (filterApply) {
      filterApply.addEventListener('click', (e) => {
        e.preventDefault();
        applyFilters();
      });
    }

    // Clear filters
    if (filterClear) {
      filterClear.addEventListener('click', (e) => {
        e.preventDefault();
        clearFilters();
      });
    }

    // Handle active filter removal
    document.querySelectorAll('[data-remove-filter]').forEach(btn => {
      btn.addEventListener('click', function() {
        const url = this.dataset.removeFilter;
        updateCollection(url);
      });
    });
  }

  // Apply filters
  function applyFilters() {
    const form = document.querySelector('[data-filters-form]');
    if (!form) return;

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (let [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }

    const url = `${window.location.pathname}?${params.toString()}`;
    updateCollection(url);
  }

  // Clear all filters
  function clearFilters() {
    const url = window.location.pathname;
    updateCollection(url);
  }

  // Setup sorting
  function setupSorting() {
    sortSelect = document.querySelector('[data-sort-select]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', function() {
      const url = updateURLParameter(window.location.href, 'sort_by', this.value);
      updateCollection(url);
    });
  }

  // Setup pagination
  function setupPagination() {
    // Load more button
    loadMoreBtn = document.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function() {
        if (isLoading) return;

        const url = this.dataset.url;
        if (url) {
          loadMoreProducts(url);
        }
      });
    }

    // Infinite scroll
    infiniteTrigger = document.querySelector('[data-infinite-trigger]');
    if (infiniteTrigger) {
      setupInfiniteScroll();
    }
  }

  // Setup infinite scroll
  function setupInfiniteScroll() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isLoading) {
          const url = infiniteTrigger.dataset.url;
          if (url) {
            loadMoreProducts(url);
          }
        }
      });
    }, {
      rootMargin: '200px'
    });

    observer.observe(infiniteTrigger);
  }

  // Load more products
  async function loadMoreProducts(url) {
    if (isLoading) return;
    // Lock IMMEDIATO (prima di qualunque logica/await): blocca i click rapidi e
    // sopprime il secondo handler delegato (bzf-filters, che controlla .loading).
    // Senza, un doppio click veloce faceva 2 fetch/append + 2 rimpiazzi di
    // paginazione => scatto del layout verso l'alto.
    isLoading = true;
    if (loadMoreBtn) loadMoreBtn.classList.add('loading');

    // Safety cap: dopo N accodamenti via AJAX si passa a una navigazione pulita
    // (DOM azzerato) invece di accumulare centinaia di card. Su iPhone il DOM che
    // cresce all'infinito col load-more satura il webview e va in errore.
    // Valore da data-max-appends (settings.beezfy_loadmore_max_pages).
    if (maxAppends === null) {
      maxAppends = parseInt((loadMoreBtn && loadMoreBtn.dataset.maxAppends) || '0', 10) || 0;
    }
    if (maxAppends > 0 && loadMoreBtn) {
      if (appendCount >= maxAppends) {
        window.location.assign(url); // carica la pagina successiva fresca
        return;
      }
      appendCount++;
    }

    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get new products
      // NAP: anche le tessere editoriali (.bzfy-grid__item--tile) vanno accodate, altrimenti sparivano dopo "Carica altro"
      const newProducts = doc.querySelectorAll('[data-product-item], .bzfy-grid__item--tile');
      const gridContainer = document.querySelector('[data-products-grid]');

      // Dedup: non ri-accodare prodotti (o tessere) gia' presenti. Evita che
      // una risposta ripetuta (cache CDN, paginazione che non avanza,
      // ordinamento che slitta mentre si importano prodotti) mostri "la
      // stessa pagina" due volte. data-tile-handle incluso per le tessere
      // (data-product-id/-handle non ce l'hanno).
      const DEDUP_SELECTOR = '[data-product-id],[data-product-handle],[data-tile-handle]';
      const keyOf = (node) => {
        const el = node.matches(DEDUP_SELECTOR) ? node : node.querySelector(DEDUP_SELECTOR);
        return el ? (el.getAttribute('data-product-id') || el.getAttribute('data-product-handle') || el.getAttribute('data-tile-handle')) : null;
      };
      const existing = new Set(
        Array.from(gridContainer.querySelectorAll(DEDUP_SELECTOR))
          .map(el => el.getAttribute('data-product-id') || el.getAttribute('data-product-handle') || el.getAttribute('data-tile-handle'))
      );
      let added = 0;
      newProducts.forEach(product => {
        const k = keyOf(product);
        if (k && existing.has(k)) return; // gia' presente: salta
        if (k) existing.add(k);
        gridContainer.appendChild(product.cloneNode(true));
        added++;
      });

      // Update pagination controls
      const newPagination = doc.querySelector('[data-pagination]');
      const currentPagination = document.querySelector('[data-pagination]');

      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
        setupPagination(); // Re-initialize pagination
      }

      // Avanzamento URL monotòno: qualunque cosa succeda alla paginazione
      // ricevuta, il prossimo caricamento punta SEMPRE alla pagina successiva
      // (page = page+1). Senza questo, se l'HTML ricevuto non ha [data-pagination]
      // il bottone resterebbe sulla stessa URL e ricaricherebbe la stessa pagina.
      if (loadMoreBtn) {
        try {
          const u = new URL(url, window.location.origin);
          const cur = parseInt(u.searchParams.get('page') || '1', 10);
          u.searchParams.set('page', String(cur + 1));
          loadMoreBtn.dataset.url = u.pathname + u.search;
        } catch (e) { /* noop */ }
        if (added === 0 && newProducts.length > 0) {
          // pagina interamente duplicata: l'URL e' gia' avanzato, il prossimo
          // click mostrera' prodotti nuovi (niente ricorsione = niente rischio loop).
          console.warn('[collection-grid] pagina duplicata saltata, avanzo a', loadMoreBtn.dataset.url);
        }
      }

      // Re-initialize ATC system for new products
      if (window.BeezfyATCSystem) {
        window.BeezfyATCSystem.init();
      }

      // Inizializza le gallery-foto delle card appena aggiunte: senza questa
      // chiamata le card del load-more non swipano (initCardGalleries parte solo
      // su DOMContentLoaded/shopify:section:load, che qui non scattano).
      if (typeof window.initCardGalleries === 'function') {
        window.initCardGalleries();
      }

    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      isLoading = false;
      if (loadMoreBtn) {
        loadMoreBtn.classList.remove('loading');
      }
    }
  }

  // Update collection with new URL
  async function updateCollection(url) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update products grid
      const newGrid = doc.querySelector('[data-products-grid]');
      const currentGrid = document.querySelector('[data-products-grid]');

      if (newGrid && currentGrid) {
        currentGrid.innerHTML = newGrid.innerHTML;
      }

      // Update active filters
      const newActiveFilters = doc.querySelector('[data-active-filters]');
      const currentActiveFilters = document.querySelector('[data-active-filters]');

      if (newActiveFilters && currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      }

      // Update URL without reload
      window.history.pushState({}, '', url);

      // Re-initialize systems
      init();

    } catch (error) {
      console.error('Error updating collection:', error);
    }
  }

  // Setup view mode toggle
  function setupViewMode() {
    const viewModeBtns = document.querySelectorAll('[data-view-mode]');

    viewModeBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const mode = this.dataset.viewMode;

        // Update grid
        if (gridElement) {
          gridElement.dataset.viewMode = mode;
        }

        // Update buttons
        viewModeBtns.forEach(b => b.classList.remove('active'));
        this.classList.add('active');

        // Save preference
        localStorage.setItem('beezfy-view-mode', mode);
      });
    });

    // Restore preference
    const savedMode = localStorage.getItem('beezfy-view-mode');
    if (savedMode && gridElement) {
      gridElement.dataset.viewMode = savedMode;
      document.querySelector(`[data-view-mode="${savedMode}"]`)?.classList.add('active');
    }
  }

  // Setup variant cards specific functionality
  function setupVariantCards() {
    // Handle variant-specific quick actions
    document.querySelectorAll('[data-variant-card="true"]').forEach(card => {
      const variantId = card.dataset.variantId;
      const productId = card.dataset.productId;

      // Ensure variant cards have proper ATC data
      const atcBtn = card.querySelector('[data-add-to-cart]');
      if (atcBtn && variantId) {
        atcBtn.dataset.variantId = variantId;
        atcBtn.dataset.productId = productId;
      }
    });
  }

  // Setup recently viewed products
  function setupRecentlyViewed() {
    const recentlyViewedGrid = document.querySelector('[data-recently-viewed-grid]');
    if (!recentlyViewedGrid) return;

    // Get recently viewed from localStorage
    const recentlyViewed = JSON.parse(localStorage.getItem('beezfy-recently-viewed') || '[]');

    if (recentlyViewed.length === 0) {
      document.querySelector('[data-recently-viewed]')?.remove();
      return;
    }

    // Fetch and display products (limit to 4)
    const productsToShow = recentlyViewed.slice(0, 4);
    // This would typically make an API call to get product data
    // For now, we'll just show the structure
  }

  // Initialize ATC System integration
  function initializeATCIntegration() {
    // Ensure all ATC buttons work with the centralized system
    document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      // The centralized ATC system will handle these automatically
      // Just ensure they have the right attributes
      if (!btn.hasAttribute('data-variant-id') && !btn.hasAttribute('data-product-id')) {
        const productItem = btn.closest('[data-product-id]');
        if (productItem) {
          btn.dataset.productId = productItem.dataset.productId;
        }
      }
    });

    // Track product views for recently viewed
    document.querySelectorAll('[data-product-item]').forEach(item => {
      const productId = item.dataset.productId;
      if (productId) {
        const link = item.querySelector('a');
        if (link) {
          link.addEventListener('click', () => {
            trackProductView(productId);
            // Also store rich entry for premium mobile menu
            try {
              const href = link.getAttribute('href');
              const title = item.querySelector('[data-product-title]')?.textContent?.trim() || link.textContent?.trim() || '';
              const img = item.querySelector('img')?.getAttribute('src') || '';
              const price = item.querySelector('[data-product-price]')?.textContent?.trim() || '';

              let rich = JSON.parse(localStorage.getItem('beezfy_recently_viewed') || '[]');
              // Remove duplicates by URL
              rich = rich.filter(p => p && p.url !== href);
              rich.unshift({ url: href, title, image: img, price });
              rich = rich.slice(0, 10);
              localStorage.setItem('beezfy_recently_viewed', JSON.stringify(rich));
            } catch {}
          });
        }
      }
    });
  }

  // Track product view
  function trackProductView(productId) {
    let recentlyViewed = JSON.parse(localStorage.getItem('beezfy-recently-viewed') || '[]');

    // Remove if already exists
    recentlyViewed = recentlyViewed.filter(id => id !== productId);

    // Add to beginning
    recentlyViewed.unshift(productId);

    // Limit to 10 items
    recentlyViewed = recentlyViewed.slice(0, 10);

    localStorage.setItem('beezfy-recently-viewed', JSON.stringify(recentlyViewed));
  }

  // Utility: Update URL parameter
  function updateURLParameter(url, param, value) {
    const urlObj = new URL(url);
    if (value) {
      urlObj.searchParams.set(param, value);
    } else {
      urlObj.searchParams.delete(param);
    }
    return urlObj.toString();
  }

  // Debounce helper
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // --- Persistenza per il BACK: salva la SNAPSHOT della griglia (HTML di tutti i prodotti già caricati +
  //     scroll) e la reinietta ALL'ISTANTE al ritorno. Niente re-fetch → nessuna attesa anche con tante pagine. ---
  function colKey(){ return 'bzfyCol:' + location.pathname + location.search; }
  function saveColState(){
    if (isRestoring) return;
    const grid = document.querySelector('[data-products-grid]');
    if (!grid) return;
    const pag = document.querySelector('[data-pagination]');
    try {
      sessionStorage.setItem(colKey(), JSON.stringify({
        grid: grid.innerHTML,
        pag: pag ? pag.innerHTML : '',
        y: window.scrollY || window.pageYOffset || 0
      }));
    } catch (e) { /* quota piena: niente snapshot (fallback senza ripristino) */ }
  }
  // Rianima UNA card ripristinata: lo snapshot salva l'HTML DOPO l'init, quindi i
  // marker sono gia' presenti (data-gallery-initialized, data-bpc-init) e i re-init
  // globali/observer li SALTANO. Togliamo i marker (nodi freschi da innerHTML: nessuna
  // istanza viva) e re-inizializziamo gallery + card v3 SOLO su questa card.
  function initOneRestoredCard(card){
    if (!card || card.dataset.bzRestored === '1') return;
    card.dataset.bzRestored = '1';
    try {
      if (window.BeeZFyCardGallery){
        card.querySelectorAll('[data-gallery-enabled]').forEach(function (g){
          g.removeAttribute('data-gallery-initialized');
          new window.BeeZFyCardGallery(g);
          g.setAttribute('data-gallery-initialized', 'true');
        });
      }
      if (window.beezfyProductCardV3 && window.beezfyProductCardV3.initCard){
        card.removeAttribute('data-bpc-init');
        window.beezfyProductCardV3.initCard(card);
      }
    } catch (e) {}
  }

  // Rianima le card ripristinate SU RICHIESTA, non tutte in blocco: lo snapshot puo'
  // contenere molte pagine (fino a beezfy_loadmore_max_pages) e inizializzarle tutte
  // in un task sincrono impallerebbe il webview iPhone (vedi crash load-more). Usiamo
  // un IntersectionObserver: ogni card viene rianimata solo quando sta per entrare nel
  // viewport (rootMargin ampio => pronta PRIMA che il dito la raggiunga, swipe/tap subito
  // reattivi). I marker restano finche' la card non e' processata => l'observer di card v3
  // la salta e non fa un bulk sincrono. Fallback a batch rAF dove manca IntersectionObserver.
  function reinitRestoredCards(grid){
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-product-card]'));
    if (!cards.length) return;

    if (window.IntersectionObserver){
      var io = new IntersectionObserver(function (entries, obs){
        entries.forEach(function (en){
          if (en.isIntersecting){ obs.unobserve(en.target); initOneRestoredCard(en.target); }
        });
      }, { rootMargin: '600px 0px' });
      cards.forEach(function (c){ io.observe(c); });
      // Rete di sicurezza: primo tocco/click su una card non ancora rianimata la
      // rianima all'istante (copre il raro caso in cui l'IO non ha ancora fatto in tempo).
      var wake = function (e){
        var card = e.target && e.target.closest && e.target.closest('[data-product-card]');
        if (card && card.dataset.bzRestored !== '1'){ if (io) io.unobserve(card); initOneRestoredCard(card); }
      };
      grid.addEventListener('touchstart', wake, { passive: true, capture: true });
      grid.addEventListener('pointerdown', wake, { capture: true });
    } else {
      var i = 0, BATCH = 6;
      var raf = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window)
              : function (fn) { return setTimeout(fn, 16); };
      (function step(){
        var end = Math.min(i + BATCH, cards.length);
        for (; i < end; i++) initOneRestoredCard(cards[i]);
        if (i < cards.length) raf(step);
      })();
    }
  }

  function restoreColState(){
    const grid = document.querySelector('[data-products-grid]');
    if (!grid) return;
    let navType = '';
    try { navType = (performance.getEntriesByType('navigation')[0] || {}).type || ''; } catch (e) {}
    if (navType !== 'back_forward') return; // solo tornando indietro
    let st = null;
    try { st = JSON.parse(sessionStorage.getItem(colKey()) || 'null'); } catch (e) {}
    if (!st || !st.grid) return;
    isRestoring = true;
    grid.innerHTML = st.grid;                       // reinietta TUTTI i prodotti già caricati (istantaneo)
    const pag = document.querySelector('[data-pagination]');
    if (pag) pag.innerHTML = st.pag || '';
    setupPagination();                              // riaggancia bottone/infinite
    reinitRestoredCards(grid);                      // rianima swipe/swatch/quickview delle card, on-demand (no freeze iPhone)
    if (window.BeezfyATCSystem && window.BeezfyATCSystem.init) { try { window.BeezfyATCSystem.init(); } catch (e) {} }
    isRestoring = false;
    // Ripristino scroll robusto: ritento mentre l'altezza pagina cresce (immagini/layout) finché raggiungo la posizione.
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
    const y = st.y || 0;
    let tries = 0;
    (function applyScroll(){
      window.scrollTo(0, y);
      if (++tries < 16 && Math.abs((window.scrollY || window.pageYOffset || 0) - y) > 4) setTimeout(applyScroll, 90);
    })();
  }
  // salva all'uscita dalla pagina e al click su un prodotto (prima di navigare)
  window.addEventListener('pagehide', saveColState);
  document.addEventListener('click', function(e){
    const a = e.target.closest('[data-products-grid] a[href*="/products/"]');
    if (a) saveColState();
  }, true);

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ init(); restoreColState(); });
  } else {
    init();
    restoreColState();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', init);

  // Expose API for external use
  window.BeezfyCollectionGrid = {
    init: init,
    updateCollection: updateCollection,
    loadMoreProducts: loadMoreProducts
  };

})();
